import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { useScope } from '../../context/ScopeContext';
import { api } from '../../lib/api';

const today = () => new Date().toISOString().slice(0, 10);
const start = () => today().slice(0, 4) + '-01-01';
const reports = [
  ['overview', 'Management overview'],
  ['service-revenue', 'Service revenue'],
  ['payments', 'Payment register'],
  ['ar-aging', 'AR aging'],
  ['professional-sessions', 'Professional sessions'],
  ['inventory-consumption', 'Inventory consumption'],
] as const;
type ReportCode = (typeof reports)[number][0];
const isReportCode = (value: string | null): value is ReportCode => reports.some(([code]) => code === value);

export function ReportingPage() {
  const scope = useScope();
  const [params, setParams] = useSearchParams();
  const requested = params.get('report');
  const [code, setCode] = useState<ReportCode>(isReportCode(requested) ? requested : 'overview');
  const [from, setFrom] = useState(start());
  const [to, setTo] = useState(today());
  useEffect(() => { if (isReportCode(requested)) setCode(requested); }, [requested]);
  const q = useQuery({
    queryKey: ['reporting', code, scope.companyId, scope.branchId, from, to],
    enabled: Boolean(scope.companyId),
    queryFn: () => {
      const p = new URLSearchParams({ companyId: scope.companyId, from, to });
      if (scope.branchId) p.set('branchId', scope.branchId);
      return api<any>('/reporting/' + code + '?' + p);
    },
  });
  const rows = Array.isArray(q.data) ? q.data : q.data ? [q.data] : [];
  const keys: string[] = Array.from(new Set<string>(rows.flatMap((row: any) => Object.keys(row).filter(key => typeof row[key] !== 'object'))));
  const changeReport = (next: ReportCode) => { setCode(next); setParams({ report: next }); };
  return <section>
    <PageHeader title="Reports" subtitle="Operational and financial reports from authoritative transaction sources" />
    <div className="card inline-form report-filter">
      <label>Report<select value={code} onChange={event => changeReport(event.target.value as ReportCode)}>{reports.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
      <label>From<input type="date" value={from} onChange={event => setFrom(event.target.value)} /></label>
      <label>To<input type="date" value={to} onChange={event => setTo(event.target.value)} /></label>
    </div>
    {q.isLoading && <p>Loading…</p>}
    {q.error && <p className="error">{(q.error as Error).message}</p>}
    {q.data && <div className="card report-results"><DataTable rows={rows} columns={keys.map(key => ({ key, header: key.replaceAll(/([A-Z])/g, ' $1'), render: (row: any) => String(row[key] ?? '—') }))} /></div>}
  </section>;
}
